import { cn } from '@repo/lib/cn';
import { useEffect, useState } from 'react';

import type { PercentageHalfCircleChartProps } from '@/components/ui/chart';

export function PercentageHalfCircleChart(percentageHalfCircleChartProps: PercentageHalfCircleChartProps) {
  const { percentage, strokeWidth = 12, className, backgroundArcClassName, progressArcClassName, labelClassName } = percentageHalfCircleChartProps;
  const [animatedPercentage, setAnimatedPercentage] = useState<number>(0);

  const viewBoxSize = 200;
  const radius: number = (viewBoxSize - strokeWidth) / 2;
  const circumference: number = Math.PI * radius;
  const strokeDasharray: number = circumference;
  const strokeDashoffset: number = circumference - (animatedPercentage / 100) * circumference;
  const strokeColor = () => {
    if (percentage > 80) return 'stroke-success';
    if (percentage > 75) return 'stroke-success-hover';
    if (percentage > 60) return 'stroke-warning';
    if (percentage > 50) return 'stroke-warning-hover';
    if (percentage > 40) return 'stroke-destructive-hover';
    if (percentage > 30) return 'stroke-destructive';

    return 'stroke-destructive-focus';
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedPercentage(Number.isNaN(percentage) ? 0 : percentage);
    }, 100);
    return () => clearTimeout(timer);
  }, [percentage]);

  return (
    <div className={cn('flex w-full flex-col items-center justify-center', className)}>
      <div className="relative w-full" style={{ aspectRatio: '2/1' }}>
        <div className={cn('pointer-events-none absolute inset-x-0 bottom-1/4 flex flex-col items-center justify-center select-none [&_p]:text-lg [&_span]:text-xs', labelClassName)}>
          <p className="text-foreground text-center font-semibold">{(!Number.isNaN(percentage) ? percentage.toPrecision(3) : '-') + '%'}</p>
        </div>
        <svg width="100%" height="100%" viewBox={`0 0 ${viewBoxSize} ${viewBoxSize / 2 + strokeWidth}`} className="overflow-visible" preserveAspectRatio="xMidYMid meet">
          {/* Background arc */}
          <path
            d={`M ${strokeWidth / 2} ${viewBoxSize / 2} A ${radius} ${radius} 0 0 1 ${viewBoxSize - strokeWidth / 2} ${viewBoxSize / 2}`}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            className={cn('stroke-muted', backgroundArcClassName)}
          />

          {/* Progress arc */}
          <path
            d={`M ${strokeWidth / 2} ${viewBoxSize / 2} A ${radius} ${radius} 0 0 1 ${viewBoxSize - strokeWidth / 2} ${viewBoxSize / 2}`}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{
              transition: 'stroke-dashoffset 1.5s ease-in-out',
            }}
            className={cn(strokeColor(), progressArcClassName)}
          />
        </svg>
      </div>
    </div>
  );
}
